/**
 * Application roles.
 *
 * Deliberately NOT called `role`: Supabase ships a top-level `role` JWT claim
 * (`authenticated` / `anon`) and this project already uses `auth.role()` inside
 * RLS policies. The application role travels as `app_metadata.app_role`, set by
 * the `public.custom_access_token_hook` database function.
 *
 * See docs/rbac-trainer-client-plan.md and
 * docs/db_migration/20260909_rbac_trainer_client.sql.
 */

export const APP_ROLES = ['client', 'trainer', 'admin'] as const

export type AppRole = (typeof APP_ROLES)[number]

/** Fail closed: an unrecognised or absent role is the least-privileged one. */
export const DEFAULT_APP_ROLE: AppRole = 'client'

export function isAppRole(value: unknown): value is AppRole {
  return typeof value === 'string' && (APP_ROLES as readonly string[]).includes(value)
}

/**
 * Roles allowed into the trainer area. `admin` is included so support can see
 * what a trainer sees; it has no extra RLS grants of its own yet.
 */
export function canAccessTrainerArea(role: AppRole): boolean {
  return role === 'trainer' || role === 'admin'
}

/**
 * Decode the `app_role` claim from an access token WITHOUT verifying it.
 *
 * Only ever call this on a token whose signature has already been checked --
 * in middleware, after `supabase.auth.getUser()` has validated the session, and
 * on the same token from that session.
 *
 * Why not `supabase.auth.getClaims()`: it verifies properly, but on a project
 * using symmetric (HS256) JWT signing it sends a request to the Auth server on
 * every call, the same as `getUser()`. Middleware already calls `getUser()` and
 * runs on nearly every route, so `getClaims()` would double auth latency
 * site-wide to re-verify a token we just verified.
 *
 * Why not `user.app_metadata.app_role` from `getUser()`: that returns
 * `auth.users.raw_app_meta_data` as stored in the database. The access token
 * hook injects the claim into the *token*, not into that column, so reading it
 * off the user object silently yields `undefined` and every account looks like
 * a client.
 */
export function decodeAppRoleFromJwt(accessToken: string | null | undefined): AppRole {
  if (!accessToken) return DEFAULT_APP_ROLE

  const payload = decodeJwtPayload(accessToken)
  if (!payload) return DEFAULT_APP_ROLE

  const appMetadata = payload.app_metadata
  if (!appMetadata || typeof appMetadata !== 'object') return DEFAULT_APP_ROLE

  const claim = (appMetadata as Record<string, unknown>).app_role
  return isAppRole(claim) ? claim : DEFAULT_APP_ROLE
}

type JwtPayload = Record<string, unknown> & { app_metadata?: unknown }

function decodeJwtPayload(token: string): JwtPayload | null {
  const segments = token.split('.')
  if (segments.length !== 3) return null

  try {
    const json = base64UrlDecode(segments[1])
    const parsed = JSON.parse(json)
    return parsed && typeof parsed === 'object' ? (parsed as JwtPayload) : null
  } catch {
    // A malformed token means no role, not a crash. getUser() is the gate that
    // decides whether the request is authenticated at all.
    return null
  }
}

/**
 * `atob` rather than `Buffer`: middleware runs on the Edge runtime, where
 * `Buffer` is not guaranteed to exist.
 */
function base64UrlDecode(segment: string): string {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}
